<template>
  <div class="upload-table-container">
    <div class="title-panel">
      <h2>{{ $t('documentUpload.uploadFiles') }}</h2>
      <div class="files-counter">{{ uploadRecords?.length }}</div>
    </div>
    <table-wrapper
      v-model:page-options="pageOptions"
      :fields="tableFields"
      :filter-configuration="filterConfiguration"
      :items="uploadRecords"
      :loading="tableBusy"
      :mode="TableMode.CLIENT_SIDE"
      :row-class="rowColors"
    >
      <template #cell(documentId)="{ data }">
        <div class="document-id-column">
          {{ data.metadata.documentId }}
        </div>
      </template>
      <template #cell(uploadType)="{ data }">
        {{ t(`documentUpload.${data.uploadType}`) }}
      </template>
      <!-- eslint-disable-next-line vue/valid-v-slot -->
      <template #cell(metadata.Filepath)="{ data }">
        <div class="filepath-column">
          <div class="filepaths-container">
            <div>
              {{ data.metadata.Filepath }}
            </div>
            <div v-if="data.renditionFile">
              {{ data.renditionFile.metadata.Filepath }}
            </div>
          </div>
          <div class="upload-status-indicators-container">
            <file-upload-status-indicator
              :upload-record="data"
              :file-upload-statuses="fileUploadStatuses"
            />
            <file-upload-status-indicator
              v-if="data.renditionFile"
              :upload-record="data.renditionFile"
              :file-upload-statuses="fileUploadStatuses"
            />
          </div>
        </div>
      </template>
      <template #cell(fileName)="{ data }">
        <div class="file-name-column">
          <div class="action-icons">
            <div class="upload-icons-container">
              <upload-icon
                :document-type="DocumentKind.MAIN"
                :upload-record="data"
                @file-uploaded="exchangeFileInRecord"
              />
              <upload-icon
                v-if="data.renditionFile"
                :document-type="DocumentKind.RENDITION"
                :upload-record="data"
                @file-uploaded="exchangeFileInRecord"
              />
            </div>
            <font-awesome-icon
              :icon="['fas', 'rectangle-list']"
              @click="openMetadataModal(data.metadata)"
            />
            <font-awesome-icon
              :icon="['fas', 'trash']"
              @click="deleteRecord(data.metadata.documentId)"
            />
            <span v-if="data.duplicate" class="text-red-500">
              ! {{ t('documentUpload.duplicateDetected') }}
            </span>
            <span v-if="data.daisyLinks?.length">
              <Button
                v-for="link in data.daisyLinks"
                :key="link"
                size="small"
                variant="text"
                severity="secondary"
                @click="openDaisyDoc(link)"
              >
                <img
                  :src="DaisyIcon"
                  :title="t('documentUpload.openInDaisy')"
                  class="w-3.5 h-3.5"
                  alt="daisy"
                />
              </Button>
            </span>
            <span v-if="data.renditionFile?.duplicate" class="text-red-500">
              ! {{ t('documentUpload.renditionDuplicateDetected') }}
            </span>
            <span v-if="data.renditionFile?.daisyLinks?.length">
              <Button
                v-for="link in data.renditionFile.daisyLinks"
                :key="link"
                size="small"
                variant="text"
                severity="secondary"
                @click="openDaisyDoc(link)"
              >
                <img
                  :src="DaisyIcon"
                  :title="t('documentUpload.openInDaisy')"
                  class="w-3.5 h-3.5"
                  alt="daisy"
                />
              </Button>
            </span>
          </div>
        </div>
      </template>
    </table-wrapper>

    <metadata-modal
      ref="modalRef"
      :metadata="metadataForModal"
      :metadata-changes="modalMetadataChanges"
      :mode="modalMode"
    />
  </div>
</template>

<script setup lang="ts">
  import { ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useToast } from '@/composables/useToast';
  import MetadataModal from '../MetadataModal/MetadataModal.vue';
  import {
    IFileUploadStatuses,
    IUploadRecord,
  } from '@/components/MassUpload/MassUpload.types';
  import { DocumentKind, IFileMetadata, IMetadataDifferences } from 'types';
  import { MetadataModalMode, UploadType } from './MassUploadTable.types';
  import { computed } from 'vue';
  import {
    RowClass,
    TableFieldsBuilder,
    TableFiltersBuilder,
    TableMode,
    useTableWrapper,
  } from '../TableWrapper';
  import { getExtensionFromFilepath } from 'utils';
  import { MetadataDiffByDocumentId } from '@/components/MassUpload/composables';
  import { Button } from 'primevue';
  import DaisyIcon from '../../assets/daisy-icon.svg';

  const { t } = useI18n();
  const { createErrorToast } = useToast();

  const metadataForModal = ref<IFileMetadata>({} as IFileMetadata);
  const modalMode = ref<MetadataModalMode>(MetadataModalMode.NORMAL);
  const modalMetadataChanges = ref<IMetadataDifferences>();

  const emit = defineEmits<{
    (e: 'updated-records', updatedRecords: IUploadRecord[]): void;
  }>();

  const props = defineProps<{
    fileUploadStatuses: IFileUploadStatuses;
    metadataChanges: MetadataDiffByDocumentId;
    tableBusy: boolean;
    uploadRecords: IUploadRecord[];
  }>();

  const { pageOptions } = useTableWrapper<IUploadRecord>(
    TableMode.CLIENT_SIDE,
    'documentId' as any,
    undefined,
    undefined,
    false,
  );
  const filterConfiguration = computed(() =>
    new TableFiltersBuilder<IUploadRecord>({
      translationKey: 'documentUpload',
    })
      .addInputFilter('documentId' as any, {
        width: '400px',
        filterProperty: 'metadata.documentId' as any,
        customLabel: t('filterableTable.filterBy', {
          field: 'id',
        }),
      })
      .addSelectFilter('uploadType', {
        width: '190px',
      })
      .addInputFilter('metadata.Filepath' as any, {
        width: '200px',
        customLabel: t('documentUpload.filepath'),
      })
      .build(),
  );
  const tableFields = computed(() =>
    new TableFieldsBuilder<IUploadRecord>({
      labelTranslationKey: 'documentUpload',
      sortable: true,
    })
      .addField('documentId' as any, {
        width: '36%',
        label: t('documentUpload.documentId'),
      })
      .addField('uploadType', {
        width: '14%',
        label: t('documentUpload.upload'),
      })
      .addField('metadata.Filepath' as any, {
        width: '23%',
        label: t('documentUpload.filepath'),
      })
      .addField('fileName' as any, {
        width: '27%',
        label: t('documentUpload.file'),
        sortable: false,
      })
      .build(),
  );

  function deleteRecord(id: string) {
    const updatedRecords = props.uploadRecords.filter(
      (record) => record?.metadata?.documentId !== id,
    );
    emit('updated-records', updatedRecords);
  }

  function exchangeFileInRecord(
    documentId: string,
    file: File,
    type: DocumentKind,
  ) {
    const indexOfRecordToUpdate = props.uploadRecords.findIndex(
      (record) => record?.metadata?.documentId === documentId,
    );
    const recordToUpdate = props.uploadRecords[indexOfRecordToUpdate];
    if (!validateExtenstion(type, recordToUpdate, file)) return;
    if (type === DocumentKind.MAIN) {
      recordToUpdate.file = file;
    } else if (type === DocumentKind.RENDITION) {
      if (!recordToUpdate.renditionFile) return;
      recordToUpdate.renditionFile.file = file;
      recordToUpdate.renditionFile.metadata!.Filepath = file.name;
    }
    emit('updated-records', props.uploadRecords);
  }

  function validateExtenstion(
    type: DocumentKind,
    uploadRecord: IUploadRecord,
    file: File,
  ): boolean {
    const extension = getExtensionFromFilepath(file.name);
    /**
     * If the upload record has a rendition file, the main file must be a PDF.
     */
    if (
      type === DocumentKind.MAIN &&
      uploadRecord.renditionFile &&
      extension !== 'pdf'
    ) {
      createErrorToast(t('documentUpload.errors.mainFileMustBePdf'));
      return false;
    }
    if (type === DocumentKind.RENDITION && extension === 'pdf') {
      createErrorToast(t('documentUpload.errors.renditionFileMustNotBePdf'));
      return false;
    }
    return true;
  }

  function rowColors(item: IUploadRecord) {
    if (item.uploadType === UploadType.UPDATED_DOCUMENT) {
      return RowClass.SELECTED_SECONDARY;
    }
    if (item.uploadType === UploadType.UPDATED_METADATA) {
      return RowClass.SELECTED;
    }
  }

  const modalRef = ref<InstanceType<typeof MetadataModal> | null>(null);
  function openMetadataModal(metadata: IFileMetadata) {
    metadataForModal.value = metadata;

    if (props.metadataChanges[metadata.documentId]) {
      modalMetadataChanges.value = props.metadataChanges[metadata.documentId];
      modalMode.value = MetadataModalMode.COMPARISON;
    } else {
      modalMode.value = MetadataModalMode.NORMAL;
    }
    modalRef.value?.show();
  }

  const openDaisyDoc = (link: string) => {
    window.open(link, '_blank');
  };
</script>

<style scoped lang="scss">
  $main-rendition-row-gap: 0.5rem;
  .upload-table-container {
    .title-panel {
      margin-top: 50px;
      display: flex;
      align-items: center;
      margin-bottom: 20px;
      column-gap: 10px;
      h2 {
        @include subheading;
      }
      .files-counter {
        align-self: flex-start;
        @include heading-counter;
      }
    }
    .document-id-column {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .filepath-column {
      display: flex;
      justify-content: space-between;
      .filepaths-container {
        display: flex;
        flex-direction: column;
        justify-content: center;
        row-gap: $main-rendition-row-gap;
      }
      .upload-status-indicators-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        row-gap: $main-rendition-row-gap;
      }
    }
    .file-name-column {
      display: flex;
      justify-content: space-between;
      column-gap: 1rem;
      .action-icons {
        .upload-icons-container {
          display: flex;
          flex-direction: column;
          row-gap: $main-rendition-row-gap;
        }
        display: flex;
        align-items: center;
        column-gap: 1rem;
        svg {
          cursor: pointer;
        }
      }
    }
  }
</style>
